import { Document, Page, View, Text, Image, Font } from '@react-pdf/renderer';
import type { RepositoryAnalysis } from '../../../components/types/analysis.types';
import { registerReportFonts } from '../sdlcReactPdf/fonts';
import { styles as s, scoreColor } from './styles';
import { formatDate, primaryLanguage, formatDuration } from './derive';
import {
  Appendix,
  CategoryTable,
  GapsList,
  MetricsRow,
  PipelineSection,
  RecommendationsGrid,
  StrengthsList,
  SummaryText,
} from './sections';

registerReportFonts();

// react-pdf hyphenates by default, chopping labels mid-word ("DE-TECTED").
// Wrap on whole words instead.
Font.registerHyphenationCallback((word) => [word]);

// Keep the header meta to a single concise line — the full language
// breakdown lives in the Files Analyzed card, so the primary language is
// enough here. A long list wraps under the health score and collides with it.
function metaLine(data: RepositoryAnalysis): string {
  return [
    `Generated ${formatDate(data.scanDate)}`,
    data.repositoryDetails?.projectType,
    primaryLanguage(data),
    data.filesCount ? `${data.filesCount} files` : '',
    formatDuration(data.scanDuration),
  ]
    .filter(Boolean)
    .join('   ·   ');
}

function Header({
  data,
  logoDataUrl,
}: {
  data: RepositoryAnalysis;
  logoDataUrl?: string | null;
}): JSX.Element {
  return (
    <View style={s.header} wrap={false}>
      <View style={s.headerLeft}>
        {logoDataUrl ? (
          <Image src={logoDataUrl} style={s.logo} />
        ) : (
          <View style={s.logoFallback}>
            <Text style={s.logoFallbackText}>A</Text>
          </View>
        )}
        <View style={s.headerTextBlock}>
          <Text style={s.headerTitle}>SDLC Assessment Report</Text>
          <Text style={s.headerRepo}>{data.repositoryName}</Text>
          <Text style={s.headerMeta}>{metaLine(data)}</Text>
        </View>
      </View>
      <View style={s.headerRight}>
        <Text style={s.healthLabel}>Health Score</Text>
        <Text style={[s.healthScore, { color: scoreColor(data.score) }]}>
          {data.score}
          <Text style={s.healthDenom}>/100</Text>
        </Text>
      </View>
    </View>
  );
}

function Footer({
  data,
  logoDataUrl,
}: {
  data: RepositoryAnalysis;
  logoDataUrl?: string | null;
}): JSX.Element {
  return (
    <View style={s.footer} wrap={false}>
      <View style={s.footerBrand}>
        {logoDataUrl ? (
          <Image src={logoDataUrl} style={s.footerLogo} />
        ) : (
          <View style={s.footerLogoFallback}>
            <Text style={s.footerLogoFallbackText}>A</Text>
          </View>
        )}
        <Text style={s.footerBrandText}>Generated by Autar</Text>
      </View>
      <Text style={s.footerText}>{formatDate(data.scanDate)}</Text>
      <Text style={s.footerDisclaimer}>
        This report is generated automatically based on repository analysis. Scores and
        recommendations are advisory and should be reviewed by your engineering team. Tool detection
        is based on file patterns and dependency analysis; some tools may not be detected if
        configured non-standardly.
      </Text>
    </View>
  );
}

export function OldReportDocument({
  data,
  logoDataUrl,
}: {
  data: RepositoryAnalysis;
  logoDataUrl?: string | null;
}): JSX.Element {
  const gaps = data.gaps ?? [];
  const strengths = data.strengths ?? [];
  const recs = data.recommendations ?? [];
  const quickWinIds = new Set((data.quickWins ?? []).map((qw) => qw.id));

  return (
    <Document title={`SDLC Assessment Report — ${data.repositoryName}`}>
      <Page size="A4" style={s.page}>
        <Header data={data} logoDataUrl={logoDataUrl} />

        <Text style={s.sectionTitle}>Executive Summary</Text>
        <MetricsRow data={data} />
        <SummaryText data={data} />

        <View style={s.divider} />

        <Text style={s.sectionTitle}>Development Pipeline</Text>
        <PipelineSection data={data} />
        <CategoryTable data={data} />

        <View break>
          <Text style={s.sectionTitle}>Findings &amp; Recommendations</Text>
          <View style={s.twoCol}>
            <View style={[s.colHalf, { paddingRight: 12 }]}>
              <Text style={s.colTitle}>Gaps</Text>
              <GapsList gaps={gaps} />
            </View>
            <View style={s.colHalf}>
              <Text style={s.colTitle}>Strengths</Text>
              <StrengthsList strengths={strengths} />
            </View>
          </View>

          <Text style={s.colTitle}>Recommendations</Text>
          <RecommendationsGrid recs={recs} quickWinIds={quickWinIds} />
        </View>

        <View break>
          <Footer data={data} logoDataUrl={logoDataUrl} />
          <Appendix data={data} />
        </View>
      </Page>
    </Document>
  );
}
